import { useCallback, useEffect, useState } from 'react'
import { Card, Col, Form, Row } from 'react-bootstrap'
import MenuItem from '@mui/material/MenuItem'
import { useDispatch } from 'react-redux'

/** TRANSLATION */
import { useTranslation } from 'react-i18next'

/** API */
import PositionApi from 'api/setting/personnel-managements/position.api'

/** STYLE */
import useStyles from 'features/setting/personnel-managements/position/style'

/** UTILS || SLICE */
import { submitModal, unSubmitModal, showModal, resetModal } from 'app/slice/modal.slice'
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'

/** COMPONENT */
import { swalActive } from 'component/Alert/Swal'
import ButtonCustom from 'component/Button/ButtonCustom'
import HeaderCard from 'component/CardCustom/HeaderCard'
import InputTextField from 'component/Input/InputTextField'
import InputTextSearch from 'component/Input/InputTextSearch'
import ModalCustom from 'component/ModalCustom/ModalCustom'
import TableCustom from 'component/Table/TableCustom'
import { notiSuccess, notiError } from 'component/notifications/notifications'
import TableRowCommon from 'component/Table/TableRowCommon'
import FilterSelect from 'component/Select/FilterSelect'
import { Icons } from 'constants/images';
import AlertModal from 'new-components/modal/alert-modal'
import SnackbarComponent from 'new-components/snackbar/snackbar-component'

const permissions = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView()
}

const initStateErrorMessage = {
  POSITION_NAME: ``,
  IS_DUPLICATE_POSITION_NAME: ``,
  POSITION_NAME_STRING_BASE: ``,
  POSITION_NAME_STRING_EMPTY: ``,
  POSITION_NAME_MAXIMUM_LENGTH: ``,
  POSITION_NAME_ANY_REQUIRED: ``,

  POSITION_NAME_EN: ``,
  IS_DUPLICATE_POSITION_NAME_EN: ``,
  POSITION_NAME_EN_STRING_BASE: ``,
  POSITION_NAME_EN_STRING_EMPTY: ``,
  POSITION_NAME_EN_MAXIMUM_LENGTH: ``,
  POSITION_NAME_EN_ANY_REQUIRED: ``
}
export default function Position() {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [positions, setPositions] = useState([])

  const [positionId, setPositionId] = useState(Number)
  const [positionName, setPositionName] = useState('')
  const [positionNameEn, setPositionNameEn] = useState('')
  const [positionStatus, setPositionStatus] = useState('')

  const [errorMessage, setErrorMessage] = useState(initStateErrorMessage)

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('createAt')
  const [sortType, setSortType] = useState('ASC')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')

  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false)
  const [statusAlertModal, setStatusAlertModal] = useState('')
  const [id, setId] = useState<number>(0)
  const [name, setName] = useState('')
  const [nameEn, setNameEn] = useState('')
  const [isOpenSnack, setIsOpenSnack] = useState(false)
  const [typeSnack, setTypeSnack] = useState<any>("success")
  const [labelSnack, setLabelSnack] = useState('')

  const loadData = useCallback(async () => {

    let condition: any = {}
    if (page) condition = { ...condition, page: page }
    if (pageLimit) condition = { ...condition, pageLimit: pageLimit }
    if (search) condition = { ...condition, search: search }
    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sortType: sortType }
    if (status) condition = { ...condition, status: status === 'ALL' ? '' : status }
    const respPositions = await PositionApi.findAll(condition)
    if (respPositions.status === 200) {
      setRowCount(respPositions.headers['x-total'])
      setPositions(respPositions.data)
    }
  }, [page, pageLimit, search, sortBy, sortType, status])

  useEffect(() => {
    loadData()
  }, [loadData])

  const submit = () => {
    if (!positionName) return setErrorMessage({ ...errorMessage, ...{ POSITION_NAME: t(`POSITION.MESSAGE.POSITION_NAME`) } })

    dispatch(submitModal())
    PositionApi.create(positionName, positionNameEn)
      .then((res: any) => {
        if (res.status !== undefined && res.status === 201) {
          resetForm()

          loadData()
          dispatch(resetModal())
          notiSuccess(t(`POSITION.MESSAGE.SUCCESS.CREATE`), '', null, null)
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`POSITION.MESSAGE.${err.message}`) } })
          dispatch(unSubmitModal())
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`POSITION.MESSAGE.${err.message}`) } })
        dispatch(unSubmitModal())
      })
  }

  const submitEdit = () => {
    if (!positionName) return setErrorMessage({ ...errorMessage, ...{ POSITION_NAME: t(`POSITION.MESSAGE.POSITION_NAME`) } })

    dispatch(submitModal())
    PositionApi.update(positionId, {
      positionName,
      positionNameEn,
      status: positionStatus
    })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 200) {
          resetForm()
          loadData()
          dispatch(resetModal())
          notiSuccess(t(`POSITION.MESSAGE.SUCCESS.UPDATE`), '', null, null)
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`POSITION.MESSAGE.${err.message}`) } })
          dispatch(unSubmitModal())
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`POSITION.MESSAGE.${err.message}`) } })
        dispatch(unSubmitModal())
      })
  }

  const clearErrorMessage = () => {
    setErrorMessage(initStateErrorMessage)
  }

  const resetForm = () => {
    setPositionId(0)
    setPositionName('')
    setPositionNameEn('')
    setPositionStatus('')

    clearErrorMessage()
  }
  const onActive = () => {
    if (id) {
      PositionApi.updateStatus(id, statusAlertModal).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('POSITION.MESSAGE.SUCCESS.UPDATE_STATUS'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('POSITION.MESSAGE.ERROR'))
        }
      })
    }
  }
  const onDelete = () => {
    if (id) {
      PositionApi.remove(id).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('POSITION.MESSAGE.SUCCESS.DELETE'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('POSITION.MESSAGE.ERROR'))
        }
      })
    }
  }

  const onEdit = (data: any) => {
    const { positionId: personalPositionId, positionName: personalPositionName, positionNameEn: personalPositionNameEn, status: personalPositionStatus } = data
    setPositionId(personalPositionId)
    setPositionName(personalPositionName)
    setPositionNameEn(personalPositionNameEn)
    setPositionStatus(personalPositionStatus)
    dispatch(showModal())
  }

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }

  const handleChangeStatus = (statusVal: string) => {
    setStatus(statusVal)
  }

  const headCells = [
    { id: 'no', disablePadding: false, label: '#' },
    { id: 'positionName', disablePadding: false, label: t('POSITION.TABLE.CELL.POSITION_NAME') },
    { id: 'positionNameEn', disablePadding: false, label: t('POSITION.TABLE.CELL.POSITION_NAME_EN') },
    { id: 'status', disablePadding: false, label: t('POSITION.TABLE.CELL.STATUS') },
    { id: 'updatedBy', disablePadding: false, label: t('POSITION.TABLE.CELL.UPDATED_BY') },
    { id: 'action', disablePadding: false, label: 'Action' }
  ]

  const renderData = (objData: any, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { positionId: personalPositionId, positionName: personalPositionName, positionNameEn: personalPositionNameEn, isDefault, updatedBy, updatedAt, status: personalPositionStatus } = objData

    const statusBtnActive = personalPositionStatus === 'ACTIVE' ? true : permissions.isUpdate.disabled
    const statusBtnInActive = personalPositionStatus === 'INACTIVE' ? true : permissions.isUpdate.disabled

    const objRenderData = {
      key: personalPositionId,
      id: personalPositionId,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: no },
        { option: 'TEXT', align: 'left', label: personalPositionName },
        { option: 'TEXT', align: 'left', label: personalPositionNameEn },
        { option: 'STATUS', align: 'center', label: personalPositionStatus },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } },
        isDefault !== `1` && {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          values: [
            { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`), disabled: statusBtnActive },
            { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`), disabled: statusBtnInActive },
            { option: 'DIVIDER', label: '', disabled: false },
            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled },
            { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: permissions.isDelete.disabled }
          ]
        },
        isDefault === `1` && { option: 'COMPONENT', align: 'center', component: <div style={{ opacity: 0.7 }}><Icons.Meatball /></div> }
      ]
    }
    return (
      <TableRowCommon
        {...objRenderData}
        onactive={() => handleSubmitModal(Number(objRenderData.id), 'ACTIVE', objRenderData.obj)}
        oninactive={() => handleSubmitModal(Number(objRenderData.id), 'INACTIVE', objRenderData.obj)}
        onedit={() => onEdit(objRenderData.obj)}
        ondelete={() => handleSubmitModal(Number(objRenderData.id), 'DELETE', objRenderData.obj)}
      />
    )
  }

  const handleSubmitModal = (id: number, status: string, obj?: any) => {
    setStatusAlertModal(status)
    setId(obj.positionId)
    setIsOpenAlertModal(true)
    setName(obj.positionName)
    setNameEn(obj.positionNameEn)
  }

  return (
    <div className={classes.root}>
      <AlertModal
        isOpen={isOpenAlertModal}
        title={t(`POSITION.ALERT.${statusAlertModal === 'DELETE' ? 'TITLE_DELETE' : 'TITLE_ACTIVE'}`)}
        message={t(`POSITION.ALERT.${statusAlertModal === 'DELETE' ? 'CONFIRM_DELETE_' : 'CONFIRM_STATUS'}`, { positionName: name, positionNameEn: nameEn && `(${nameEn})`, status: t(`POSITION.ALERT.${statusAlertModal}`) })}
        type={statusAlertModal === 'DELETE' ? 'error' : 'warning'}
        handleClose={() => setIsOpenAlertModal(false)}
        onCancel={{ onClick: () => setIsOpenAlertModal(false), title: t('BUTTON.CANCEL') }}
        onSubmit={{ onClick: statusAlertModal === 'DELETE' ? onDelete : onActive, title: t('BUTTON.SUBMIT') }}
      />
      <SnackbarComponent
        onOpen={isOpenSnack}
        onClose={() => setIsOpenSnack(false)}
        label={labelSnack}
        type={typeSnack}
        transform={`translateX(calc(100vw - 100% - 32px)) translateY(calc(-100vh + 100% + ${window.innerHeight - 50}px))`}
        autoHideTime={3000}
      />
      <Card className="border-0 h-100">
        <HeaderCard text={t('POSITION.TITLE')} />
        <Card.Body>
          <Card.Title className={'mb-0 pl-xl-3'}>
            <Row className="align-items-center">
              <Col md={8}>
                <Row className="flax-row">
                  <Col sm={12} md={6} xl={4} className="pt-2">
                    <InputTextSearch
                      keyInput={'search'}
                      label={t('POSITION.SEARCH')}
                      value={search}
                      onchange={(event) => {
                        setSearch(event.target.value)
                        setPage(1)
                      }}
                      onClear={(event) => {
                        setSearch('')
                        setPage(1)
                      }}
                    />
                  </Col>
                  <Col sm={12} md={6} xl={4} className="pl-md-0 pt-2">
                    <FilterSelect
                      onchange={(event) => {
                        const value = event.target.value
                        if (value) {
                          handleChangeStatus(value)
                          setPage(1)
                        }
                      }}
                      renderValue={() => `${t('STATUS.LABEL')}: ${t(`STATUS.${status}`)}`}
                      label={t('STATUS.LABEL')}
                      selectId="select-status"
                      value={status}
                      labelId="label-status"
                      options={[
                        <MenuItem key="1" value="ALL">
                          {t('STATUS.ALL')}
                        </MenuItem>,
                        <MenuItem key="2" value="ACTIVE">
                          {t('STATUS.ACTIVE')}
                        </MenuItem>,
                        <MenuItem key="3" value="INACTIVE">
                          {t('STATUS.INACTIVE')}
                        </MenuItem>
                      ]}
                    />
                  </Col>
                </Row>
              </Col>
              <Col md={4} className="mt-2 mt-md-auto">
                <div className="pr-xl-2">
                  <ButtonCustom mode="add" disabled={permissions.isCreate.disabled} onClick={() => dispatch(showModal())} textButton={t('POSITION.BUTTON.ADD')} className="w-auto ml-auto d-flex mt-auto" />
                </div>
              </Col>
            </Row>
          </Card.Title>

          <div className={'mt-3 pl-xl-3 pr-xl-2'}>
            <TableCustom
              page={page}
              pageLimit={pageLimit}
              sortType={sortType}
              sortBy={sortBy}
              onSort={onRequestSort}
              setPageLimit={handleChangeRowsPerPage}
              setPage={handleChangePage}
              rowCount={rowCount}
              headCells={headCells}
              rowsData={positions.map((val, i) => {
                return renderData(val, i)
              })}
            />
          </div>
        </Card.Body>
      </Card>

      <ModalCustom
        title={positionId ? t('POSITION.TITLE_UPDATE') : t('POSITION.TITLE_CREATE')}
        component={
          <div>
            {positionId ? (
              <div>
                <Form className="mx-0 mt-0 mb-3 p-0  d-inline-flex w-100">
                  <Form.Label className=" mr-auto">{t('INPUT.LABEL_STATUS')}</Form.Label>
                  <Form.Check type="switch" id="custom-switch" onChange={() => setPositionStatus(positionStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')} checked={positionStatus === 'ACTIVE' ? true : false} style={{ marginLeft: 'auto' }} />
                </Form>
              </div>
            ) : (
              ''
            )}
            <Row>
              <Col sm={12} className="mb-3">
                <InputTextField
                  onchange={(event) => {
                    setPositionName(event.target.value)
                    clearErrorMessage()
                  }}
                  value={positionName}
                  label={t('POSITION.INPUT.POSITION_NAME')}
                  helperText={
                    errorMessage.POSITION_NAME ||
                    errorMessage.IS_DUPLICATE_POSITION_NAME ||
                    errorMessage.POSITION_NAME_STRING_BASE ||
                    errorMessage.POSITION_NAME_STRING_EMPTY ||
                    errorMessage.POSITION_NAME_MAXIMUM_LENGTH ||
                    errorMessage.POSITION_NAME_ANY_REQUIRED
                  }
                  required={true}
                />
              </Col>
              <Col sm={12} className="mb-3">
                <InputTextField
                  key="positionNameEn"
                  onchange={(event) => {
                    setPositionNameEn(event.target.value)
                    clearErrorMessage()
                  }}
                  value={positionNameEn}
                  helperText={
                    errorMessage.POSITION_NAME_EN ||
                    errorMessage.IS_DUPLICATE_POSITION_NAME_EN ||
                    errorMessage.POSITION_NAME_EN_STRING_BASE ||
                    errorMessage.POSITION_NAME_EN_STRING_EMPTY ||
                    errorMessage.POSITION_NAME_EN_MAXIMUM_LENGTH ||
                    errorMessage.POSITION_NAME_EN_ANY_REQUIRED
                  }
                  label={t('POSITION.INPUT.POSITION_NAME_EN')}
                  required={false}
                />
              </Col>
            </Row>
          </div>
        }
        onReset={resetForm}
        onSubmit={positionId ? submitEdit : submit}
        textBtnCancel={t('POSITION.BUTTON.CANCEL')}
        textBtnConfirm={t('POSITION.BUTTON.SAVE')}
      />
    </div>
  )
}
